import { getFormatDate, addOnUnloadCallback, IS_SNAPSHOT, safeJsonStringify } from './utils';
import { sendBeaconToDocsLog, sendBeaconToDocsStatistic } from './io/beacon';
import metaData, { extendMeta } from './metaData';
import { isDebugMode, forceLogLevelQuery, LogLevel, PostLogLevel, isDocs } from './config';

// IMP: Attention 循环引用
import { getNextSeq } from './logger';

const logQueue: any[] = [];
let flushTimer: number;

export function getReqSeqByLogQueue(logQueue: any) {
  let seq = logQueue[0].attributes.seq;

  if (logQueue.length > 1) {
    seq += `-${logQueue[logQueue.length - 1].attributes.seq}`;
  }
  return seq;
}

export function flushLog() {
  if (logQueue.length === 0) {
    return;
  }

  const res = sendBeaconToDocsLog(logQueue);
  if (!res) {
    console.warn('[DocLogger]上报失败：beacon');
  }

  logQueue.length = 0;
}

function postLog(data: Record<string, any>) {
  if (data && data.attributes && data.attributes.log.length > 1500) {
    // 清空存量日志
    flushLog();
    // 单独发这条过大的日志
    logQueue.push(data);
    flushLog();
  } else {
    logQueue.push(data);
    // 减少日志积压，从15条改为10条
    if (logQueue.length >= 10) {
      flushLog();
    }
  }

  flushTimer && clearTimeout(flushTimer);
  flushTimer = window.setTimeout(() => {
    flushLog();
    clearTimeout(flushTimer);
  }, 6 * 1000);
}

//按照key统计日志类型数量
const logProfile: {
  [key: string]: number;
} = {};

const LOCAL_LOG: any[] = [];

export function getLocalLog() {
  return LOCAL_LOG;
}

export default class Reporter {
  constructor() {}

  /**
   * 日志上报
   * @param subModulePrefix
   * @param type
   * @param logArgs
   */
  public doLog(subModulePrefix: string, type: string, logArgs: any[], logSeg: number) {
    //调试环境或者没有达到上报等级或者截图模式不上报
    const needPost = !(IS_SNAPSHOT || isDebugMode() || LogLevel[type] < PostLogLevel);

    //debug Mode均输出 & queryString _logLevel以上的会输出
    const needConsole = isDebugMode() || LogLevel[type] >= LogLevel[forceLogLevelQuery];

    if (needConsole) {
      if (type === 'warn' || type === 'error') {
        console[type].apply(null, [
          `[${type}]${subModulePrefix ? `(${subModulePrefix})` : ''} ${getFormatDate()} \n`,
          ...logArgs.map(item => {
            return typeof item === 'object' ? safeJsonStringify(item) : item;
          })
        ]);
      } else {
        console[type].apply(null, [
          `[${type}]${subModulePrefix ? `(${subModulePrefix})` : ''}`,
          ...logArgs
        ]);
      }
    }

    const structuringLog: {
      key?: string;
      key2?: string;
      cost?: number;
    } = {};
    //启发式取结构化日志数据key,key2,cost
    logArgs.forEach(value => {
      if (typeof value === 'string') {
        if (!structuringLog.key) {
          structuringLog.key = value;
        } else if (!structuringLog.key2) {
          structuringLog.key2 = value;
        }
      } else if (typeof value === 'number' && structuringLog.cost === undefined) {
        structuringLog.cost = value;
      }
    });

    try {
      const nowTime = new Date();
      const data = {
        appName: metaData.productName,
        attributes: {
          ...metaData,
          url: location.href.replace('https://docs.corp.kuaishou.com', ''), // 每次上报最新的url
          seq: logSeg,
          type,
          module: subModulePrefix,
          fTime: getFormatDate(nowTime),
          time: nowTime.getTime(),
          log: safeJsonStringify(logArgs),
          runTime: nowTime.getTime() - metaData.timeNvStart,
          ...structuringLog
        },
        localDateTime: nowTime.getTime()
      };

      const logKey = structuringLog.key2 || structuringLog.key || 'unknow';
      logProfile[logKey] = logProfile[logKey] ? logProfile[logKey] + 1 : 1;

      // 无论需不需要上报，本地日志存全量
      LOCAL_LOG.push(data);

      if (needPost) {
        postLog(data);
      }
    } catch (e) {
      console.error('[DocLogger]上报失败：', e);
    }
  }

  /**
   * 统计上报
   * @param type
   * @param subModulePrefix
   * @param logName
   * @param logData
   */
  private doStatistic(
    type: 'COUNTER' | 'GAUGE',
    logName: string,
    logDataOrDigest?: number | string,
    gaugeDigest?: string
  ) {
    if (!logName) {
      console.error('没有指定logName');
      return;
    }
    if (!metaData.productName) {
      console.error('没有productName进行了统计');
    }
    const data: {
      event: string;
      type: 'COUNTER' | 'GAUGE';
      value?: any;
      docId?: string;
      userId?: string;
      nvLang?: string;
      nationalityCode?: string;
      assetsPrefix: string;
      digest?: string; // 统计数据摘要
      product: string; // 工程id
    } = {
      event: `${metaData.productName}.${logName}`,
      type,
      docId: metaData.docId,
      userId: metaData.userId,
      nvLang: metaData.nvLang,
      nationalityCode: metaData.ip_subnet,
      assetsPrefix: metaData.assetsPrefix || '',
      product: metaData.productName || ''
    };
    if (type === 'COUNTER') {
      data.type = 'COUNTER';
      if (typeof logDataOrDigest === 'string') {
        data.digest = logDataOrDigest;
      }
    } else if (type === 'GAUGE') {
      data.type = 'GAUGE';
      data.value = 0;
      if (typeof logDataOrDigest === 'number') {
        data.value = logDataOrDigest;
      }
      if (typeof gaugeDigest === 'string') {
        data.digest = gaugeDigest;
      }
    }

    //非docs项目不上报统计
    if (IS_SNAPSHOT || isDebugMode() || !isDocs) {
      console.info(`【统计】[${logName}]:`, logDataOrDigest);
    } else {
      sendBeaconToDocsStatistic(data);
    }
  }

  /**
   * 计数
   * @param logName
   */
  public count(logName: string, digest?: string) {
    this.doStatistic('COUNTER', logName, digest);
  }

  /**
   * 统计
   * @param logName
   * @param value
   */
  public gague(logName: string, value: number, digest?: string) {
    this.doStatistic('GAUGE', logName, value, digest);
  }
}

export const defaultReporter = new Reporter();

addOnUnloadCallback(function () {
  extendMeta({
    closeTime: new Date().getTime(),
    isUnload: 'OnUnload-2'
  });
  const nextSeq = getNextSeq();
  defaultReporter.doLog('logSdk', 'info', ['logCount', nextSeq - 1, logProfile], nextSeq);
  flushLog();
});
